import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import FileUpload from '@/components/FileUpload';

const AdminSiteContent = () => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/site-content`);
      setContent(res.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContent((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API}/admin/site-content`, content, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Content saved!');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-site-content">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Site Content</h1>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          data-testid="save-content-btn"
          className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full"
        >
          {isSaving ? 'Saving...' : <><Save size={18} className="mr-2" /> Save Changes</>}
        </Button>
      </div>

      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <h2 className="font-heading text-xl text-[#2C2C2C] mb-6 pb-4 border-b border-[#E8DFD0]">Hero Section</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Tagline</Label>
              <Input
                name="hero_tagline"
                data-testid="hero-tagline-input"
                value={content?.hero_tagline || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Subtitle</Label>
              <Input
                name="hero_subtitle"
                data-testid="hero-subtitle-input"
                value={content?.hero_subtitle || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <h2 className="font-heading text-xl text-[#2C2C2C] mb-6 pb-4 border-b border-[#E8DFD0]">About Section</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">About Title</Label>
              <Input
                name="about_title"
                data-testid="about-title-input"
                value={content?.about_title || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">About Content</Label>
              <Textarea
                name="about_content"
                data-testid="about-content-input"
                value={content?.about_content || ''}
                onChange={handleInputChange}
                className="input-elegant min-h-[150px]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Mission Statement</Label>
              <Textarea
                name="about_mission"
                data-testid="about-mission-input"
                value={content?.about_mission || ''}
                onChange={handleInputChange}
                className="input-elegant min-h-[100px]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Values</Label>
              <Textarea
                name="about_values"
                data-testid="about-values-input"
                value={content?.about_values || ''}
                onChange={handleInputChange}
                className="input-elegant min-h-[100px]"
              />
            </div>
            <div>
              <FileUpload
                currentUrl={content?.about_image || ''}
                onUpload={(url) => setContent(prev => ({ ...prev, about_image: url }))}
                accept="image/*"
                label="Upload About Section Image"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <h2 className="font-heading text-xl text-[#2C2C2C] mb-6 pb-4 border-b border-[#E8DFD0]">Contact Information</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Email</Label>
              <Input
                name="contact_email"
                type="email"
                data-testid="contact-email-input"
                value={content?.contact_email || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Phone</Label>
              <Input
                name="contact_phone"
                data-testid="contact-phone-input"
                value={content?.contact_phone || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Address</Label>
              <Input
                name="contact_address"
                data-testid="contact-address-input"
                value={content?.contact_address || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <h2 className="font-heading text-xl text-[#2C2C2C] mb-6 pb-4 border-b border-[#E8DFD0]">Social Media Links</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Instagram URL</Label>
              <Input
                name="social_instagram"
                data-testid="social-instagram-input"
                value={content?.social_instagram || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Facebook URL</Label>
              <Input
                name="social_facebook"
                data-testid="social-facebook-input"
                value={content?.social_facebook || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Pinterest URL</Label>
              <Input
                name="social_pinterest"
                data-testid="social-pinterest-input"
                value={content?.social_pinterest || ''}
                onChange={handleInputChange}
                className="input-elegant"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSiteContent;
